import { baseApi } from "./api";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { IPayApiResponse } from "../interfaces/Ibilling";

const BILLING_ROUTES = {
  SUBSCRIPTIONS: "/billing/subscriptions?reservation_id=:reservation_id",
  CHECKOUT_SUCCESS: "/billing/checkout/success?session_id=:session_id",
  PAYMENT_METHODS: "/billing/payment-methods",
  PAYMENT_METHOD_DEFAULT: "/billing/payment-methods/:id/default",
  PAYMENT_METHOD_DELETE: "/billing/payment-methods/:id",
  SUBSCRIPTION_ITEMS:
    "/billing/subscriptions/:subscription_id/subscription-items?reservation_id=:reservation_id",
  PAY: "/billing/pay",
  SETUP_INTENTS: "/billing/setup-intents",
  BUSINESS_LINE_PACKAGES: "/billing/business-line-packages",
  CHANGE_PACKAGE: "/billing/business-line-packages/:id",
  INVOICES: "/billing/invoices",
  INVOICE_DETAILS: "/billing/invoice-details",
  INVOICE_PDF: "/billing/invoices/:id/pdf",
  TRANSACTIONS: "/billing/transactions",
  BUSINESS_LINE_PACKAGE_DELETE: "/billing/business-line-packages/:id",
  PAYMENT_ISSUE: "/billing/payment-issue",
};

const billingApi = {
  getPaymentIssue: async () => {
    const response = await baseApi.get(BILLING_ROUTES.PAYMENT_ISSUE);
    return response.data;
  },
  deleteSubscriptionPackage: async (subscription_id: string) => {
    const url = BILLING_ROUTES.BUSINESS_LINE_PACKAGE_DELETE.replace(
      ":id",
      subscription_id
    );
    const response = await baseApi.delete(url);
    return response.data;
  },
  setupIntents: async ({ client_secret }: { client_secret: string }) => {
    const response = await baseApi.post(BILLING_ROUTES.SETUP_INTENTS, {
      client_secret,
    });
    return response.data;
  },
  getInvoices: async () => {
    const response = await baseApi.get(BILLING_ROUTES.INVOICES);
    return response.data;
  },
  getInvoiceDetails: async () => {
    const response = await baseApi.get(BILLING_ROUTES.INVOICE_DETAILS);
    return response.data;
  },
  getInvoicePdf: async (id: string) => {
    const url = BILLING_ROUTES.INVOICE_PDF.replace(":id", id);
    const response = await baseApi.get(url, {
      responseType: "blob", // Important for PDF files
    });
    return response;
  },
  getTransactions: async () => {
    const response = await baseApi.get(BILLING_ROUTES.TRANSACTIONS);
    return response.data;
  },
  createSubscription: async (reservation_id: string) => {
    const url = BILLING_ROUTES.SUBSCRIPTIONS.replace(
      ":reservation_id",
      reservation_id
    );
    const response = await baseApi.post(url);
    return response;
  },
  createSubscriptionItem: async (
    subscription_id: string,
    reservation_id: string
  ) => {
    const url = BILLING_ROUTES.SUBSCRIPTION_ITEMS.replace(
      ":subscription_id",
      subscription_id
    ).replace(":reservation_id", reservation_id);
    const response = await baseApi.post(url);
    return response;
  },
  checkoutSuccess: async (session_id: string) => {
    const url = BILLING_ROUTES.CHECKOUT_SUCCESS.replace(
      ":session_id",
      session_id
    );
    const response = await baseApi.get(url);
    return response;
  },
  getPaymentMethods: async () => {
    const response = await baseApi.get(BILLING_ROUTES.PAYMENT_METHODS);
    return response.data.payment_methods;
  },
  setDefaultPaymentMethod: async (id: string) => {
    const url = BILLING_ROUTES.PAYMENT_METHOD_DEFAULT.replace(":id", id);
    const response = await baseApi.post(url);
    return response.data;
  },
  deletePaymentMethod: async (id: string) => {
    const url = BILLING_ROUTES.PAYMENT_METHOD_DELETE.replace(":id", id);
    const response = await baseApi.delete(url);
    return response.data;
  },
  pay: async (reservation_id: string): Promise<IPayApiResponse> => {
    const response = await baseApi.post(BILLING_ROUTES.PAY, {
      reservation_id,
    });
    return response.data;
  },
  getBusinessLinePackages: async () => {
    const response = await baseApi.get(BILLING_ROUTES.BUSINESS_LINE_PACKAGES);
    return response.data;
  },
  changePackage: async (
    id: string,
    new_package_id: string,
    effective_from: string
  ) => {
    const url = BILLING_ROUTES.CHANGE_PACKAGE.replace(":id", id);

    const response = await baseApi.put(url, {
      new_package_id,
      effective_from,
    });
    return response.data;
  },
};

// React Query hooks
function useGetPaymentIssue() {
  return useQuery({
    queryKey: ["paymentIssue"],
    queryFn: billingApi.getPaymentIssue,
  });
}

export function useSetupIntents() {
  return useMutation({
    mutationFn: ({ client_secret }: { client_secret: string }) =>
      billingApi.setupIntents({ client_secret }),
  });
}
export function useGetInvoices() {
  return useQuery({
    queryKey: ["invoices"],
    queryFn: billingApi.getInvoices,
  });
}

export function useGetInvoicePdf(id: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ["invoicePdf", id],
    queryFn: () => billingApi.getInvoicePdf(id),
    enabled: options?.enabled !== false,
  });
}

export function useGetTransactions() {
  return useQuery({
    queryKey: ["transactions"],
    queryFn: billingApi.getTransactions,
  });
}

export function useCreateSubscription() {
  return useMutation({
    mutationFn: (reservation_id: string) =>
      billingApi.createSubscription(reservation_id),
  });
}

export function useCreateSubscriptionItem() {
  return useMutation({
    mutationFn: ({
      subscription_id,
      reservation_id,
    }: {
      subscription_id: string;
      reservation_id: string;
    }) => billingApi.createSubscriptionItem(subscription_id, reservation_id),
  });
}

export function useCheckoutSuccess(session_id: string) {
  return useQuery({
    queryKey: ["checkoutSuccess", session_id],
    queryFn: () => billingApi.checkoutSuccess(session_id),
  });
}

export function usePaymentMethods(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ["paymentMethods"],
    queryFn: billingApi.getPaymentMethods,
    ...options,
  });
}

export function useSetDefaultPaymentMethod() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => billingApi.setDefaultPaymentMethod(id),
    onSuccess: () => {
      // Invalidate and refetch payment methods to update UI
      queryClient.invalidateQueries({ queryKey: ["paymentMethods"] });
    },
  });
}

export function useDeletePaymentMethod() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => billingApi.deletePaymentMethod(id),
    onSuccess: () => {
      // Invalidate and refetch payment methods to update UI
      queryClient.invalidateQueries({ queryKey: ["paymentMethods"] });
    },
  });
}

export function usePay() {
  return useMutation({
    mutationFn: (reservation_id: string) => billingApi.pay(reservation_id),
  });
}

export function useGetBusinessLinePackages() {
  return useQuery({
    queryKey: ["businessLinePackages"],
    queryFn: billingApi.getBusinessLinePackages,
  });
}

export function useChangePackage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      new_package_id,
      effective_from,
    }: {
      id: string;
      new_package_id: string;
      effective_from: string;
    }) => billingApi.changePackage(id, new_package_id, effective_from),
    onSuccess: () => {
      // Invalidate and refetch business line packages to update UI
      queryClient.invalidateQueries({ queryKey: ["businessLinePackages"] });
    },
  });
}

export function useDeleteSubscriptionPackage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (subscription_id: string) =>
      billingApi.deleteSubscriptionPackage(subscription_id),
    onSuccess: () => {
      // Invalidate and refetch business line packages to update UI
      queryClient.invalidateQueries({ queryKey: ["businessLinePackages"] });
    },
  });
}

export function useGetInvoiceDetails() {
  return useQuery({
    queryKey: ["invoiceDetails"],
    queryFn: billingApi.getInvoiceDetails,
  });
}

export { billingApi };
export const billingApiHooks = {
  useGetInvoiceDetails,
  useCreateSubscription,
  useCreateSubscriptionItem,
  useCheckoutSuccess,
  usePaymentMethods,
  useSetDefaultPaymentMethod,
  useDeletePaymentMethod,
  usePay,
  useGetBusinessLinePackages,
  useChangePackage,
  useGetInvoices,
  useGetInvoicePdf,
  useGetTransactions,
  useSetupIntents,
  useDeleteSubscriptionPackage,
  useGetPaymentIssue,
} as const;
